import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { useSessionJwt } from './useSessionJwt';

const EMPTY_PARAMS = {};

/**
 * useFetchData - Centralized data fetching hook for all pages
 *
 * Automatically monitors URL-driven global selectors and refetches when they change:
 * - selectedEnvironment (from ?env= query param via EnvironmentContext)
 * - selectedApp (from /app/:selectedApp route param)
 * - selectedRange (from ?range= query param via TimeRangeContext)
 *
 * @param {Object} options
 * @param {string} options.endpoint - API endpoint path (e.g., '/data/sessions')
 * @param {Object} options.params - Optional additional query parameters
 * @param {boolean} options.enabled - Optional flag to enable/disable fetching (default: true)
 * @returns {Object} { data, loading, error, refetch }
 */
export const useFetchData = ({
  endpoint,
  params = EMPTY_PARAMS,
  enabled = true
}) => {
  // Extract URL-driven global selector values
  const { selectedEnvironment } = useEnvironment();
  const { selectedRange } = useTimeRange();
  const { selectedAppId } = useParams();

  // Canonical auth consumption: key on the stable sessionId, read the JWT at
  // call time. Keying on the JWT string refetched EVERY page's data on every
  // Stytch heartbeat (~4 min) — the token rotates even though nothing changed.
  const { sessionId, getJwt, revalidateSession } = useSessionJwt();

  // State management
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const serializedParams = useMemo(() => (
    JSON.stringify(
      Object.entries(params || {})
        .filter(([, value]) => value !== null && value !== undefined)
        .sort(([a], [b]) => a.localeCompare(b))
    )
  ), [params]);

  // Build query parameters
  const queryParams = useMemo(() => {
    const allParams = new URLSearchParams();

    // v2 endpoints (/v2/...) use 'env'/'range'; v1 endpoints use 'environment'/'time_window'
    if (endpoint?.startsWith('/v2/')) {
      if (selectedEnvironment) allParams.set('env', selectedEnvironment);
      if (selectedRange) allParams.set('range', selectedRange);
    } else {
      if (selectedEnvironment) allParams.set('environment', selectedEnvironment);
      if (selectedRange) allParams.set('time_window', selectedRange);
    }

    // Add custom params
    JSON.parse(serializedParams).forEach(([key, value]) => {
      allParams.set(key, value);
    });

    return allParams.toString();
  }, [endpoint, selectedEnvironment, selectedRange, serializedParams]);

  // Latest in-flight request's controller. Each new fetch aborts its
  // predecessor (last-REQUESTED wins, not last-resolved), and the effect's
  // cleanup aborts on unmount so no setState can fire afterwards.
  const abortRef = useRef(null);

  // Fetch function
  const fetchData = useCallback(async () => {
    // JWT read fresh at call time — never a dependency
    const sessionJwt = getJwt();

    // Skip if disabled or missing required auth or endpoint
    if (!enabled || !sessionJwt || !endpoint) {
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;
    const { signal } = abortController;

    try {
      setLoading(true);
      setError(null);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const url = `${apiUrl}${endpoint}${queryParams ? `?${queryParams}` : ''}`;

      let response = await fetch(url, {
        signal,
        headers: {
          'Authorization': `Bearer ${sessionJwt}`,
          'Content-Type': 'application/json'
        }
      });

      // 401 is an auth event, not a data error: force a session round-trip.
      // Dead session → local state clears → ProtectedRoute shows login
      // (instead of the page rendering with silently-empty data). Live
      // session → JWT was refreshed; retry once with the new token.
      if (response.status === 401 && !signal.aborted) {
        const alive = await revalidateSession();
        if (!alive || signal.aborted) return;
        response = await fetch(url, {
          signal,
          headers: {
            'Authorization': `Bearer ${getJwt()}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!signal.aborted) setData(result);
    } catch (err) {
      if (err.name === 'AbortError' || signal.aborted) {
        return; // Request was aborted (superseded or unmounted) — no state updates
      }
      console.error(`Failed to fetch ${endpoint}:`, err);
      setError(err.message);
    } finally {
      // finally runs on the abort path too — guard so an aborted request
      // can't flip loading state after unmount or under a newer request.
      if (!signal.aborted) setLoading(false);
    }
  }, [enabled, sessionId, getJwt, revalidateSession, endpoint, queryParams]);

  // Auto-fetch when dependencies change; abort in-flight work on unmount/change
  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  // Manual refetch function
  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
};
